// API base URL — in dev, Vite proxies /v1 and /auth to the backend.
// In production, the frontend is on a different domain so we need the full URL.
const API_BASE = import.meta.env.VITE_API_BASE || "";

// --- Types (mirroring proto messages) ---

export interface WriterProfile {
  userId: string;
  displayName: string;
  bio: string;
  avatarUrl: string;
  articleCount: number;
  tagline: string;
  socialLinks: Record<string, string>;
  subscriberCount: number;
}

export interface Article {
  id: string;
  writerId: string;
  title: string;
  subtitle: string;
  coverImageUrl: string;
  previewMarkdown: string;
  priceUsdc: string;
  priceEurc: string;
  published: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  writer: WriterProfile | null;
  contentMarkdown?: string;
  category: string;
  topics: string[];
}

export interface ArticleContent {
  id: string;
  contentMarkdown: string;
}

export interface WriterDashboard {
  writer: WriterProfile;
  walletAddress: string;
  totalArticles: number;
  publishedArticles: number;
  totalReads: number;
  totalEarnedUsdc: string;
  totalEarnedEurc: string;
}

export interface Topic {
  name: string;
  count: number;
}

export interface Comment {
  id: string;
  articleId: string;
  parentId: string;
  commenterAddress: string;
  body: string;
  likeCount: number;
  createdAt: string;
  updatedAt: string;
  replies: Comment[];
}

export interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
}

// --- Fetch helpers ---

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public headers?: Headers,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (!res.ok) {
    throw new ApiError(res.status, await res.text(), res.headers);
  }
  return res.json();
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` };
}

// --- Public API ---

export async function listArticles(
  opts: { limit?: number; category?: string; topic?: string; writerId?: string; subscriberAddress?: string; sort?: string } = {},
): Promise<Article[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.category) params.set("category", opts.category);
  if (opts.topic) params.set("topic", opts.topic);
  if (opts.writerId) params.set("writerId", opts.writerId);
  if (opts.subscriberAddress) params.set("subscriberAddress", opts.subscriberAddress);
  if (opts.sort) params.set("sort", opts.sort);
  const qs = params.toString();
  const data = await request<{ articles: Article[] }>(
    `/v1/articles${qs ? `?${qs}` : ""}`,
  );
  return data.articles || [];
}

export async function getArticle(id: string): Promise<Article> {
  return request<Article>(`/v1/articles/${id}`);
}

export interface ContentResponse {
  status: "ok" | "payment-required";
  content?: ArticleContent;
  paymentRequired?: string; // raw PAYMENT-REQUIRED header value
}

export async function getArticleContent(
  id: string,
  payerAddress?: string,
  paymentSignature?: string,
): Promise<ContentResponse> {
  const headers: Record<string, string> = {};
  if (payerAddress) headers["X-Payer-Address"] = payerAddress;
  if (paymentSignature) headers["PAYMENT-SIGNATURE"] = paymentSignature;

  const res = await fetch(`${API_BASE}/v1/articles/${id}/content`, {
    headers: { "Content-Type": "application/json", ...headers },
  });

  if (res.status === 402) {
    const paymentHeader = res.headers.get("PAYMENT-REQUIRED") || "";
    return { status: "payment-required", paymentRequired: paymentHeader };
  }
  if (!res.ok) {
    throw new ApiError(res.status, await res.text(), res.headers);
  }
  const content: ArticleContent = await res.json();
  return { status: "ok", content };
}

export async function getWriterProfile(
  writerId: string,
): Promise<WriterProfile> {
  return request<WriterProfile>(`/v1/writers/${writerId}`);
}

export async function searchTopics(query: string, limit = 10): Promise<Topic[]> {
  const params = new URLSearchParams({ query, limit: String(limit) });
  const data = await request<{ topics: Topic[] }>(`/v1/topics?${params}`);
  return data.topics || [];
}

export async function subscribe(
  writerId: string,
  subscriberAddress: string,
): Promise<void> {
  await request(`/v1/writers/${writerId}/subscribe`, {
    method: "POST",
    body: JSON.stringify({ subscriberAddress }),
  });
}

export async function unsubscribe(
  writerId: string,
  subscriberAddress: string,
): Promise<void> {
  await request(`/v1/writers/${writerId}/unsubscribe`, {
    method: "POST",
    body: JSON.stringify({ subscriberAddress }),
  });
}

export async function isSubscribed(
  writerId: string,
  subscriberAddress: string,
): Promise<boolean> {
  const params = new URLSearchParams({ subscriberAddress });
  const data = await request<{ subscribed: boolean }>(
    `/v1/writers/${writerId}/subscribed?${params}`,
  );
  return data.subscribed;
}

// --- Comments API ---

export async function listComments(
  articleId: string,
  sort = "newest",
  limit = 50,
): Promise<{ comments: Comment[]; totalCount: number; writerAddress: string }> {
  const params = new URLSearchParams({ sort, limit: String(limit) });
  const data = await request<{ comments: Comment[]; totalCount: number; writerAddress: string }>(
    `/v1/articles/${articleId}/comments?${params}`,
  );
  return { comments: data.comments || [], totalCount: data.totalCount || 0, writerAddress: data.writerAddress || "" };
}

export async function createComment(
  articleId: string,
  commenterAddress: string,
  body: string,
  parentId?: string,
  jwt?: string,
): Promise<Comment> {
  const headers: Record<string, string> = {};
  if (jwt) Object.assign(headers, authHeaders(jwt));
  const data = await request<{ comment: Comment }>(
    `/v1/articles/${articleId}/comments`,
    {
      method: "POST",
      body: JSON.stringify({ commenterAddress, body, parentId: parentId || "" }),
      headers,
    },
  );
  return data.comment;
}

export async function likeComment(
  commentId: string,
  likerAddress: string,
  jwt?: string,
): Promise<number> {
  const headers: Record<string, string> = {};
  if (jwt) Object.assign(headers, authHeaders(jwt));
  const data = await request<{ likeCount: number }>(
    `/v1/comments/${commentId}/like`,
    {
      method: "POST",
      body: JSON.stringify({ likerAddress }),
      headers,
    },
  );
  return data.likeCount;
}

export async function deleteComment(
  commentId: string,
  commenterAddress: string,
  jwt?: string,
): Promise<void> {
  const headers: Record<string, string> = {};
  if (jwt) Object.assign(headers, authHeaders(jwt));
  await request(`/v1/comments/${commentId}/delete`, {
    method: "POST",
    body: JSON.stringify({ commenterAddress }),
    headers,
  });
}

// --- Auth API ---

export async function sendOtp(email: string): Promise<void> {
  await request("/auth/v1/otp", {
    method: "POST",
    body: JSON.stringify({ email, type: 1 }), // OTP_TYPE_LOGIN
  });
}

export async function verifyOtp(
  email: string,
  code: string,
): Promise<AuthTokenResponse> {
  return request<AuthTokenResponse>("/auth/v1/verify", {
    method: "POST",
    body: JSON.stringify({ email, code, type: 1 }), // OTP_TYPE_LOGIN
  });
}

export async function refreshToken(
  token: string,
): Promise<AuthTokenResponse> {
  return request<AuthTokenResponse>("/auth/v1/token", {
    method: "POST",
    body: JSON.stringify({ refreshToken: token }),
  });
}

export async function signOut(token: string, jwt: string): Promise<void> {
  await request("/auth/v1/logout", {
    method: "POST",
    body: JSON.stringify({ refreshToken: token }),
    headers: authHeaders(jwt),
  });
}

// --- Writer API (JWT-authenticated) ---

export async function onboardWriter(
  jwt: string,
  displayName: string,
  bio: string,
): Promise<WriterProfile> {
  return request<WriterProfile>("/v1/writer/onboard", {
    method: "POST",
    body: JSON.stringify({ displayName, bio }),
    headers: authHeaders(jwt),
  });
}

export async function updateWriterProfile(
  jwt: string,
  data: {
    displayName: string;
    tagline: string;
    bio: string;
    socialLinks: Record<string, string>;
  },
): Promise<WriterProfile> {
  return request<WriterProfile>("/v1/writer/profile", {
    method: "PATCH",
    body: JSON.stringify(data),
    headers: authHeaders(jwt),
  });
}

export async function createArticle(
  jwt: string,
  data: {
    title: string;
    subtitle: string;
    coverImageUrl: string;
    contentMarkdown: string;
    previewMarkdown: string;
    priceUsdc: string;
    priceEurc: string;
    category: string;
    topicNames: string[];
  },
): Promise<Article> {
  return request<Article>("/v1/writer/articles", {
    method: "POST",
    body: JSON.stringify(data),
    headers: authHeaders(jwt),
  });
}

export async function updateArticle(
  jwt: string,
  id: string,
  data: {
    title: string;
    subtitle: string;
    coverImageUrl: string;
    contentMarkdown: string;
    previewMarkdown: string;
    priceUsdc: string;
    priceEurc: string;
    category: string;
    topicNames: string[];
  },
): Promise<Article> {
  return request<Article>(`/v1/writer/articles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
    headers: authHeaders(jwt),
  });
}

export async function publishArticle(jwt: string, id: string): Promise<Article> {
  return request<Article>(`/v1/writer/articles/${id}:publish`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: authHeaders(jwt),
  });
}

export async function unpublishArticle(jwt: string, id: string): Promise<Article> {
  return request<Article>(`/v1/writer/articles/${id}:unpublish`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: authHeaders(jwt),
  });
}

export async function listWriterArticles(
  jwt: string,
  limit = 50,
): Promise<Article[]> {
  const data = await request<{ articles: Article[] }>(
    `/v1/writer/articles?limit=${limit}`,
    { headers: authHeaders(jwt) },
  );
  return data.articles || [];
}

export async function getWriterArticle(
  jwt: string,
  id: string,
): Promise<Article> {
  return request<Article>(`/v1/writer/articles/${id}`, {
    headers: authHeaders(jwt),
  });
}

export async function getWriterDashboard(
  jwt: string,
): Promise<WriterDashboard> {
  return request<WriterDashboard>("/v1/writer/dashboard", {
    headers: authHeaders(jwt),
  });
}
